/**
 * 支払準備の月次の手順。
 *
 * SFT Hub（keiri-task-manager）の支払準備画面と同じ 8 ステップを、同じ順・同じ言葉で
 * 並べる。動かす処理の名前は API と支払業務 GAS の許可リストに合わせる。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 支払準備の 8 ステップ（レポート作成 → 確認 → 証憑整理 → 未着チェック → 確定版 → freee 修正 → リマインド → 全銀・口座照合）と、結果の文言・フォルダの仕分けを持つ
 * @graph-connects none
 */

/** 支払のサイクル */
export type PaymentCycle = '15日' | '25日';

/** @graph-connects none */
export const PAYMENT_CYCLES: readonly PaymentCycle[] = ['15日', '25日'];

/**
 * ステップで押せること。
 *
 * - `job`: GAS で時間のかかる処理を動かす（レポート作成・証憑整理）
 * - `remind`: 対象を確認してから Slack へ送る
 * - `sheet`: 最新の支払管理レポートを開く
 * - `link`: 外の画面を開く
 */
export type PaymentStepAction =
  | { type: 'job'; fn: (cycle: PaymentCycle) => string; label: (cycle: PaymentCycle) => string }
  | { type: 'remind'; label: (cycle: PaymentCycle) => string }
  | { type: 'sheet'; label: string }
  | { type: 'link'; href: string; label: string };

/**
 * ステップの作業の種類。画面では種類ごとに色を分ける。
 *
 * - `run`: ボタンで GAS を動かす
 * - `check`: スプレッドシートを目で見て直す
 * - `freee`: freee の画面で操作する
 */
export type PaymentStepTone = 'run' | 'check' | 'freee';

/** ステップのまとまり（並べる順） */
/** @graph-connects none */
export const PAYMENT_PHASES = ['レポート', '証憑', '支払'] as const;

/** 支払準備の 1 ステップ */
export interface PaymentStep {
  /** 番号 */
  no: string;
  /** 見出し */
  title: string;
  /** 誰がどこで作業するか（実行・目視・freee 操作など） */
  how: string;
  /** 作業の種類（色分けに使う） */
  tone: PaymentStepTone;
  /** まとまり */
  phase: (typeof PAYMENT_PHASES)[number];
  /** 一行の説明 */
  description: string;
  /** 詳しい手順・注意点（HTML。ManualView で危ないタグを落として描く） */
  detail: string;
  /** 押せること */
  action: PaymentStepAction;
}

/**
 * 振込前チェック（口座照合）。Python の別サービスで、ログインが SFT Hub に依存するため
 * 当面は SFT Hub の画面から使う（docs/ado/task/README.md の「支払準備」）
 * @graph-connects none
 */
export const PAYMENT_CHECK_URL = 'https://keiri-task-manager-ten.vercel.app/';

/** @graph-connects none */
const evidenceJob: PaymentStepAction = {
  type: 'job',
  fn: (cycle) => `証憑整理${cycle}`,
  label: (cycle) => `証憑整理を実行（${cycle}支払）`,
};

/** @graph-connects none */
export const PAYMENT_STEPS: readonly PaymentStep[] = [
  {
    no: '1',
    title: 'レポート作成',
    how: '実行',
    tone: 'run',
    phase: 'レポート',
    description: 'freee から未払取引を取得して、新しい支払管理レポートを作ります（数分かかります）',
    detail: `<h3>何が起こるか</h3>
<ul>
  <li>新しいスプレッドシート「<code>YYYYMMDD_HHMM_支払管理レポート</code>」が作成されます</li>
  <li>freee から未払取引を全件取得し、14 列のレポートに自動整形されます</li>
  <li>同じファイル内に「経費支払用」シート（従業員ごとの経費合計）も作られます</li>
</ul>
<blockquote>処理中に他のボタンを連打しないでください。freee のレート制限でエラーになる可能性があります。</blockquote>
<p>レポートは実行のたびに新しいファイルとして作成されます。必ず最新版（上の一覧の先頭）で以降の作業を進めてください。</p>`,
    action: { type: 'job', fn: () => 'レポート作成', label: () => 'レポート作成を実行' },
  },
  {
    no: '2',
    title: '内容確認（必要なら freee 修正 → STEP 1 を再実行）',
    how: '目視・freee',
    tone: 'check',
    phase: 'レポート',
    description: 'スプレッドシートで「振込元口座」が赤い行と、区分の誤りを確認します',
    detail: `<h3>チェックポイント</h3>
<ul>
  <li><b>M 列（振込元口座）が赤く強調</b>されている行 → 取引先に支払元口座が未設定。下のルールで freee に設定する</li>
  <li><b>I 列（区分）の値が想定と違う</b>行 → プルダウンで正しい区分に変更</li>
</ul>
<h3>M 列が赤いとき＝振込元口座の設定ルール（上から順に）</h3>
<table><tr><th>取引先の種類</th><th>設定する口座</th></tr>
<tr><td>15日払いの取引先</td><td><b>15日払い</b></td></tr>
<tr><td>商品仕入れにかかる取引先</td><td><b>りそな</b></td></tr>
<tr><td>通常の支払い（仕入以外）</td><td><b>みずほ</b></td></tr></table>
<p>freee →「取引先」→ 該当取引先 →「支払元口座」を設定。設定後に STEP 1 を再実行すると赤が消えます。</p>
<p>修正があったら：freee を直す → STEP 1 を再実行 → 新しいレポートで再確認、を問題が無くなるまで繰り返します。</p>`,
    action: { type: 'sheet', label: 'スプレッドシートを開く' },
  },
  {
    no: '3',
    title: '証憑整理（1 回目：確認用）',
    how: '実行',
    tone: 'run',
    phase: '証憑',
    description: '対象取引の証憑 PDF を月次フォルダへコピーします（1 回目は「確認用」フォルダ）',
    detail: `<h3>フォルダ名の付き方</h3>
<pre>202605_15日支払_確認用_…　← 1 回目（同名フォルダが無いとき）
202605_15日支払_確定版_…　← 2 回目以降（STEP 5）</pre>
<ul>
  <li>25日支払は「みずほ」「りそな」で別フォルダに分けて出力されます</li>
  <li>15日支払は「区分＝⓪15日払い かつ 決済期日が15日以前」の行だけが対象です</li>
  <li>「⑤証憑未着」「⑥来月支払分」の行は対象外（自動スキップ）</li>
</ul>
<p>STEP 3 と STEP 5 は同じボタンです。1 回目は「確認用」、2 回目は「確定版」が自動で付きます。</p>`,
    action: evidenceJob,
  },
  {
    no: '4',
    title: '証憑未着チェック',
    how: '目視・編集',
    tone: 'check',
    phase: '証憑',
    description:
      'H 列リンクで証憑 PDF を開いて確認し、請求書でない行は I 列を「⑤証憑未着」に変更します',
    detail: `<h3>操作手順（スプレッドシート上）</h3>
<ol>
  <li>H 列（証憑 ID）の青字リンクをクリック → Drive 上の証憑 PDF が新しいタブで開く</li>
  <li>内容を確認。<b>請求書でない場合</b>（仮申請のダミー等）→ その行の I 列を「<b>⑤証憑未着</b>」に変更</li>
  <li>請求書なら何もしなくて OK。すべてチェックし終わったら STEP 5 へ</li>
</ol>
<blockquote>このステップは完全に目視作業です（仮申請かは証憑の中身でしか判断できないため自動化していません）。</blockquote>`,
    action: { type: 'sheet', label: 'スプレッドシートを開く' },
  },
  {
    no: '5',
    title: '証憑整理（2 回目：確定版）',
    how: '実行',
    tone: 'run',
    phase: '証憑',
    description: 'もう一度実行すると「確定版」フォルダが作られます（⑤証憑未着の行は自動で除外）',
    detail: `<h3>何が起こるか</h3>
<ul>
  <li>STEP 3 と同じ処理ですが、同名フォルダが既にあるため「<b>確定版</b>」として作成されます</li>
  <li>「⑤証憑未着」にした行はコピー対象から自動的に除外されます</li>
</ul>
<p>完了したら確定版フォルダの中身を確認してください（STEP 8 の口座照合で使います）。</p>`,
    action: {
      ...evidenceJob,
      label: (cycle) => `証憑整理を再実行（${cycle}支払）`,
    },
  },
  {
    no: '6',
    title: 'freee で「⑤証憑未着」分を修正',
    how: 'freee 操作',
    tone: 'freee',
    phase: '支払',
    description: '未着にした取引を freee で確認・修正します（相殺・再計上はしない運用です）',
    detail: `<h3>操作手順（freee 画面）</h3>
<ol>
  <li>freee 会計 →「取引」→「取引の一覧」</li>
  <li>該当の取引を検索（取引先名や日付で絞り込み）</li>
  <li>内容に誤りがあれば修正（金額・取引先・日付・勘定科目など）</li>
</ol>
<blockquote><b>相殺・再計上（翌月への振替）は行いません</b>（複雑でミスが起きやすいため廃止）。</blockquote>`,
    action: {
      type: 'link',
      href: 'https://secure.freee.co.jp/deals',
      label: 'freee 取引一覧を開く',
    },
  },
  {
    no: '7',
    title: 'Slack リマインド送信',
    how: '実行（送信前に確認あり）',
    tone: 'run',
    phase: '支払',
    description:
      '証憑が未着の取引先への送付依頼を Slack に通知します。送信前に対象一覧が表示されます',
    detail: `<h3>対象・締切</h3>
<ul>
  <li>対象：レポートで「⑤証憑未着」のままの取引先（15日分は M 列「15日」、25日分は「みずほ／りそな」）</li>
  <li>証憑送付の締切：15日支払分は<b>10日まで</b>／25日支払分は<b>15日まで</b>（過ぎると翌月支払い）</li>
</ul>
<p>送信ボタンを押すと、まず対象取引先の一覧が表示されます。送信を押したときだけ送られます。</p>`,
    action: { type: 'remind', label: (cycle) => `リマインドを送信（${cycle}分）` },
  },
  {
    no: '8',
    title: '全銀ファイル DL → 担当者へ送付 → 口座照合',
    how: 'freee・照合',
    tone: 'freee',
    phase: '支払',
    description:
      'freee で対象を絞って「一括振込」→ 全銀ファイルを DL。口座照合は SFT Hub の「振込前チェック」で行います',
    detail: `<h3>操作手順（freee 画面）</h3>
<ol>
  <li><b>振込先口座番号が空白</b>の取引先について freee で口座番号を登録</li>
  <li>15日払い分は I 列＝⓪15日払い・B 列＝期日15日まで に絞って確認</li>
  <li><b>振込元口座を選択</b>（15日払い→「15日」／りそな分→「りそな」／みずほ分→「みずほ」）</li>
  <li>支払期日（翌月第 2 月曜日まで）で絞り込み → 全選択 →「一括振込」→ 全銀式ファイル（.txt）が DL される</li>
</ol>
<p>確定版フォルダの証憑 PDF と全銀ファイルの突合は「振込前チェック」で自動化できます（3 段階判定）。振込前チェックは当面 SFT Hub で使います。支払実行は支払担当者の作業です。</p>`,
    action: { type: 'link', href: PAYMENT_CHECK_URL, label: '振込前チェックを開く（SFT Hub）' },
  },
];

/** フォルダの仕分け先（並べる順） */
/** @graph-connects none */
export const FOLDER_GROUPS = ['15日', 'りそな', 'みずほ', 'その他'] as const;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 証憑整理フォルダを名前で 15日 / りそな / みずほ / その他 に仕分ける
 * @graph-connects none
 */
export function folderGroupOf(name: string): (typeof FOLDER_GROUPS)[number] {
  if (name.includes('りそな')) {
    return 'りそな';
  }
  if (name.includes('みずほ')) {
    return 'みずほ';
  }
  return name.includes('15日') ? '15日' : 'その他';
}

/** 結果から開ける場所 */
export interface ResultLink {
  /** 表示名 */
  label: string;
  /** 開く URL */
  url: string;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 結果のオブジェクトから文字列の項目を読む。無ければ空文字
 * @graph-connects none
 */
function text(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value : '';
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business ジョブが終わったときの文言と、作ったレポート・フォルダへのリンクを組み立てる。25日支払はみずほ・りそなの 2 フォルダを並べる
 * @graph-connects none
 */
export function summarizeJobResult(
  title: string,
  result: Record<string, unknown> | null,
): { message: string; links: ResultLink[] } {
  const r = result ?? {};
  const lines = [`${title}が完了しました`];
  const fileName = text(r, 'fileName');
  if (fileName !== '') {
    const count = typeof r['件数'] === 'number' ? String(r['件数']) : '?';
    lines.push(`${fileName}（${count}件）`);
  }
  if (typeof r.copiedCount === 'number') {
    lines.push(`証憑 ${String(r.copiedCount)} 件をコピー（${text(r, '接尾辞')}）`);
  }

  const links: ResultLink[] = [];
  if (text(r, 'fileUrl') !== '') {
    links.push({ label: 'レポートを開く', url: text(r, 'fileUrl') });
  }
  if (text(r, 'folderUrl') !== '') {
    links.push({ label: text(r, 'folderName') || 'フォルダ', url: text(r, 'folderUrl') });
  }
  for (const bank of ['みずほ', 'りそな']) {
    const sub = r[bank];
    if (typeof sub === 'object' && sub !== null) {
      const folder = sub as Record<string, unknown>;
      if (text(folder, 'folderUrl') !== '') {
        links.push({ label: text(folder, 'folderName') || bank, url: text(folder, 'folderUrl') });
      }
    }
  }
  return { message: lines.join('\n'), links };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business リマインドの対象確認の結果から、送る相手の取引先名を取り出す
 * @graph-connects none
 */
export function remindTargetsOf(result: unknown): string[] {
  if (typeof result !== 'object' || result === null) {
    return [];
  }
  const list = (result as Record<string, unknown>)['取引先一覧'];
  return Array.isArray(list) ? list.filter((name): name is string => typeof name === 'string') : [];
}
